import { useEffect, useRef, useState } from "react";
import { Globe, X } from "lucide-react";
import posthog from "posthog-js";
import { useLocation } from "wouter";
import { useTranslation } from "react-i18next";
import { supportedLanguages } from "@/locales";
import { useLanguageRouter } from "@/hooks/useLanguageRouter";

// Deliberately NOT an auto-redirect: Googlebot and users always get the URL
// they asked for, and switching languages is an explicit one-click choice.
// Strings are hardcoded per-language (not i18next) because the banner must
// render in the *suggested* language while the page itself is still English,
// without pulling that language's ~90KB translation chunk just for one line.
const BANNER_STRINGS: Record<
  string,
  { message: string; accept: string; dismiss: string }
> = {
  es: {
    message: "Este sitio está disponible en español.",
    accept: "Ver en español",
    dismiss: "Seguir en inglés",
  },
  fr: {
    message: "Ce site est disponible en français.",
    accept: "Voir en français",
    dismiss: "Continuer en anglais",
  },
  de: {
    message: "Diese Website ist auf Deutsch verfügbar.",
    accept: "Auf Deutsch ansehen",
    dismiss: "Weiter auf Englisch",
  },
  pt: {
    message: "Este site está disponível em português.",
    accept: "Ver em português",
    dismiss: "Continuar em inglês",
  },
  it: {
    message: "Questo sito è disponibile in italiano.",
    accept: "Vedi in italiano",
    dismiss: "Continua in inglese",
  },
  ja: {
    message: "このサイトは日本語でもご利用いただけます。",
    accept: "日本語で見る",
    dismiss: "英語のまま続ける",
  },
  ko: {
    message: "이 사이트는 한국어로도 이용할 수 있습니다.",
    accept: "한국어로 보기",
    dismiss: "영어로 계속",
  },
  zh: {
    message: "本网站提供简体中文版。",
    accept: "查看中文版",
    dismiss: "继续使用英文",
  },
  id: {
    message: "Situs ini tersedia dalam Bahasa Indonesia.",
    accept: "Lihat dalam Bahasa Indonesia",
    dismiss: "Lanjutkan dalam bahasa Inggris",
  },
  nl: {
    message: "Deze site is beschikbaar in het Nederlands.",
    accept: "Bekijk in het Nederlands",
    dismiss: "Doorgaan in het Engels",
  },
  pl: {
    message: "Ta strona jest dostępna po polsku.",
    accept: "Zobacz po polsku",
    dismiss: "Kontynuuj po angielsku",
  },
  tr: {
    message: "Bu site Türkçe olarak da mevcut.",
    accept: "Türkçe görüntüle",
    dismiss: "İngilizce devam et",
  },
  vi: {
    message: "Trang web này có phiên bản tiếng Việt.",
    accept: "Xem bằng tiếng Việt",
    dismiss: "Tiếp tục bằng tiếng Anh",
  },
};

const STORAGE_KEY = "localeSuggestionChoice";

// Surfaces where a language prompt is noise or the localized route doesn't
// exist (/cli, /skill, /unsubscribe have no /:lang variants; checkout, admin
// and the /mcp-auth app-connection consent step should never be interrupted).
const EXCLUDED_FIRST_SEGMENTS = new Set([
  "admin",
  "checkout",
  "cli",
  "cli-auth",
  "mcp-auth",
  "skill",
  "unsubscribe",
  "404",
]);

function readStoredChoice(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

function storeChoice(value: string) {
  try {
    localStorage.setItem(STORAGE_KEY, value);
  } catch {
    // Private mode / blocked storage: the banner may reappear next visit,
    // which is acceptable.
  }
}

function detectSuggestedLanguage(): string | null {
  if (typeof navigator === "undefined") return null;
  // QA override: ?lang-suggest=fr forces the banner regardless of browser
  // language, so the flow can be tested without changing OS/browser locale.
  const override = new URLSearchParams(window.location.search).get(
    "lang-suggest",
  );
  if (override && BANNER_STRINGS[override]) return override;
  // Primary browser language only: if someone's first preference is English,
  // suggesting their second-choice language would be presumptuous.
  const primary = (navigator.languages?.[0] || navigator.language || "")
    .slice(0, 2)
    .toLowerCase();
  return BANNER_STRINGS[primary] ? primary : null;
}

export default function LanguageSuggestionBanner() {
  const [location] = useLocation();
  const { i18n } = useTranslation();
  const { changeLanguage } = useLanguageRouter();
  const [hidden, setHidden] = useState(false);
  const shownTrackedRef = useRef(false);

  // Evaluated once per page load: the audience is visitors landing on an
  // unprefixed (English) URL whose browser prefers a language we support.
  const [suggestedLang] = useState<string | null>(() => {
    if (readStoredChoice()) return null;
    return detectSuggestedLanguage();
  });

  const pathname = location.split(/[?#]/)[0] || "/";
  const firstSegment = pathname.split("/").filter(Boolean)[0] ?? "";
  const onUnprefixedUrl = !supportedLanguages.includes(firstSegment as any);
  const onExcludedSurface = EXCLUDED_FIRST_SEGMENTS.has(firstSegment);

  const visible =
    !!suggestedLang && !hidden && onUnprefixedUrl && !onExcludedSurface;

  useEffect(() => {
    if (visible && !shownTrackedRef.current) {
      shownTrackedRef.current = true;
      posthog.capture("language_suggestion_banner_shown", {
        suggested_language: suggestedLang,
      });
    }
  }, [visible, suggestedLang]);

  if (!visible) return null;

  const strings = BANNER_STRINGS[suggestedLang];

  const handleAccept = () => {
    posthog.capture("language_suggestion_banner_accepted", {
      suggested_language: suggestedLang,
    });
    storeChoice(`accepted:${suggestedLang}`);
    setHidden(true);
    if (i18n.language !== suggestedLang) {
      i18n.changeLanguage(suggestedLang);
    }
    changeLanguage(suggestedLang);
  };

  const handleDismiss = () => {
    posthog.capture("language_suggestion_banner_dismissed", {
      suggested_language: suggestedLang,
    });
    storeChoice("dismissed");
    setHidden(true);
  };

  return (
    <div className="fixed bottom-20 left-1/2 z-40 w-[calc(100%-2rem)] max-w-[520px] -translate-x-1/2 lg:bottom-6">
      <div
        className="relative flex items-center gap-3 rounded-[14px] py-3 pl-4 pr-10"
        style={{
          background:
            "linear-gradient(150deg, #12131c 0%, #0c0d15 55%, #14101e 100%)",
          border: "1px solid rgba(255,255,255,0.07)",
          boxShadow:
            "0 24px 60px -20px rgba(0,0,0,0.7), inset 0 1px 0 rgba(255,255,255,0.04)",
        }}
      >
        <Globe className="h-5 w-5 shrink-0 text-[#9ca3b4]" strokeWidth={1.8} />
        <div className="min-w-0 flex-1">
          <p className="m-0 text-[13px] font-medium text-white">
            {strings.message}
          </p>
          <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1">
            <button
              onClick={handleAccept}
              className="text-[13px] font-bold text-[#d946ef] transition-colors hover:text-[#ec4899]"
            >
              {strings.accept}
            </button>
            <button
              onClick={handleDismiss}
              className="text-[12px] font-medium text-[#8a92a6] transition-colors hover:text-white"
            >
              {strings.dismiss}
            </button>
          </div>
        </div>
        <button
          onClick={handleDismiss}
          aria-label="Dismiss"
          className="absolute right-2.5 top-2.5 flex h-[28px] w-[28px] items-center justify-center rounded-full text-[#5b6272] transition-colors hover:bg-white/[0.08] hover:text-[#d1d5e0]"
        >
          <X className="h-4 w-4" strokeWidth={2.2} />
        </button>
      </div>
    </div>
  );
}
